import { useTranslation } from 'react-i18next';

import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { Box, Drawer, IconButton, Stack, Typography } from '@mui/material';

import { EmptyState, ErrorState, LoadingState } from '@/shared/ui';

import { useAccessHistory } from '../../hooks';

export interface AccessHistoryDrawerProps {
  open: boolean;
  onClose: () => void;
}

/**
 * The append-only change trail, opened beside the review rather than as a separate destination.
 *
 * <p>It is the answer to "what has already been dealt with", which is half of a review: the reviewer
 * should never have to leave the screen — and lose their filters and their place — to check whether the
 * revoke they just made was recorded.
 *
 * <p>Nothing is fetched until it is opened. The history is the largest read on the screen and most
 * reviews never need it.
 */
export function AccessHistoryDrawer({ open, onClose }: AccessHistoryDrawerProps) {
  const { t } = useTranslation('access');
  const history = useAccessHistory(open ? { size: 100 } : {});

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: { xs: '100%', sm: 480 }, maxWidth: '100%' } } }}
    >
      <Stack spacing={1.5} sx={{ p: 2 }}>
        <Stack direction="row" alignItems="flex-start" spacing={1}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6">{t('review.historyTitle')}</Typography>
            <Typography variant="caption" color="text.secondary">
              {t('review.historyHint')}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose} aria-label={t('common:action.close')}>
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Stack>
        {history.isLoading ? (
          <LoadingState label={t('review.historyLoading')} />
        ) : history.isError ? (
          <ErrorState title={t('review.historyError')} message={history.error?.message ?? ''} />
        ) : (history.data?.items ?? []).length === 0 ? (
          <EmptyState title={t('review.historyEmpty')} />
        ) : (
          (history.data?.items ?? []).map((event) => (
            <Box key={event.id} sx={{ borderBottom: 1, borderColor: 'divider', pb: 1 }}>
              <Typography variant="body2" fontWeight={600}>
                {event.action}
                {event.target ? ` · ${event.target}` : ''}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {new Date(event.occurredAt).toLocaleString()} ·{' '}
                {event.actorUserId ?? t('review.platformActor')}
              </Typography>
            </Box>
          ))
        )}
      </Stack>
    </Drawer>
  );
}
